/**
 * Page chrome: the brand header, the sitewide alert bar, the page body, the footer, and the
 * floating "Back to Top" button.
 *
 * Every page renders it itself except the three redirect-only pages, which render nothing and
 * send the browser on with `window.location.href`: `pages/california-consumer-privacy-policy-act/`,
 * `pages/metal-solutions/industries/medical-systems/` and
 * `pages/metal-solutions/industries/packaging-and-processing-equipment/`. A page passes the
 * Contentful header, locations and footer data that
 * its `getStaticProps` fetched with `allContentfulHeaderCalls()` (`@ryerson/frontend.contentful`),
 * picked out with `nextUtilities.ts` → `getHeaderData`:
 * `<HeaderLayout {...getHeaderData(props)}>`. A page that skips that call has no header data,
 * and the full header can't render.
 *
 * The header's location list is the entry for the visitor's `localization.country` from
 * `useApplication()`. `headerType="simple"` (`SimpleHeader` / `SimpleFooter`) exists, but every
 * page that renders `HeaderLayout` uses the default `"full"`.
 *
 * Related: `Shared/AlertBar/AlertBar.tsx` (inside `#root`, above the page),
 * `Shared/BackToTop/BackToTop.tsx`; the store's own copy is
 * `web-store-ui/application/src/components/NextLayouts/HeaderLayout.tsx`.
 */
import React from "react";
import { HeaderDataType } from "@ryerson/frontend.header-footer";
import {
	StructuredFullFooter,
	SimpleHeader,
	StructuredFullHeader,
	SimpleFooter,
	LocationCountries,
	HeaderLocationData,
	FooterDataObject,
} from "@ryerson/frontend.header-footer";
import { useApplication } from "@ryerson/frontend.application";
import BackToTop from "@components/Shared/BackToTop/BackToTop";
import AlertBar from "@components/Shared/AlertBar/AlertBar";

interface HeaderLayoutProps {
	children?: any;
	// "full" (default): Contentful-driven header and footer. "simple": the minimal header and
	// footer, which take no Contentful data. No page passes "simple".
	headerType?: "full" | "simple";
	// Declared but not read by the component.
	background?: string;
	ryersonHeaderData: HeaderDataType;
	ryersonLocationData: HeaderLocationData;
	ryersonFooterData: FooterDataObject;
}

/** Renders the page chrome around `children`; see the file header. */
const HeaderLayout: React.FC<HeaderLayoutProps> = ({
	children,
	headerType = "full",
	ryersonHeaderData,
	ryersonLocationData,
	ryersonFooterData,
}) => {
	const {
		localization: { country },
	} = useApplication();
	const companyName: string = process.env.NEXT_PUBLIC_COMPANY as string;

	return (
		<>
			<BackToTop buttonLabel={""} />
			{headerType === "full" ? (
				<StructuredFullHeader
					companyName={companyName}
					headerData={ryersonHeaderData}
					locationData={ryersonLocationData.countries[country as keyof LocationCountries]}
				/>
			) : null}

			{headerType === "simple" ? (
				<SimpleHeader companyName={companyName} storeDirectory="" />
			) : null}

			<div id="root">
				<AlertBar />
				{children}

				{headerType === "full" ? (
					<StructuredFullFooter
						companyName={companyName}
						footerData={ryersonFooterData}
					/>
				) : null}

				{headerType === "simple" ? (
					<SimpleFooter companyName={companyName} storeDirectory="" />
				) : null}
			</div>
		</>
	);
};

export default HeaderLayout;
